import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Plus, MoreVertical, Package, Eye, X, ChevronDown, ChevronRight, 
  Users, FileText, DollarSign, TrendingUp, Calendar, XCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useAlerts } from '../context/AlertContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockIssuance = () => {
  const navigate = useNavigate();
  const [issuances, setIssuances] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [viewingIssuance, setViewingIssuance] = useState(null);
  const [issuanceToCancel, setIssuanceToCancel] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [filterCustomer, setFilterCustomer] = useState('all');
  const { canCreateIssuance, canDelete, canViewPrices } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useSettings();
  const { fetchAlerts } = useAlerts();

  const [formData, setFormData] = useState({
    customer_id: '',
    quotation_number: '',
    location_id: '',
    notes: '',
    items: []
  });

  const [newItem, setNewItem] = useState({
    part_id: '',
    quantity: 1,
    selling_price: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [issuancesRes, customersRes, locationsRes, partsRes] = await Promise.all([
        axios.get(`${API}/stock-issuances`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/parts`)
      ]);
      setIssuances(issuancesRes.data);
      setCustomers(customersRes.data);
      setLocations(locationsRes.data);
      setParts(partsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      quotation_number: '',
      location_id: '',
      notes: '',
      items: []
    });
    resetNewItem();
  };

  const resetNewItem = () => {
    setNewItem({
      part_id: '',
      quantity: 1,
      selling_price: 0
    });
  };

  const handleAddItem = () => {
    if (!newItem.part_id) {
      toast.error('Select a part');
      return;
    }
    
    const part = parts.find(p => p.id === newItem.part_id);
    if (!part) return;

    if (newItem.quantity > part.quantity) {
      toast.error(`Insufficient stock. Available: ${part.quantity}`);
      return;
    }

    const item = {
      part_id: newItem.part_id,
      part_reference: part.reference_number,
      part_description: part.description,
      quantity: newItem.quantity,
      buying_price: part.buying_price,
      selling_price: newItem.selling_price || part.selling_price
    };

    setFormData({
      ...formData,
      items: [...formData.items, item]
    });
    resetNewItem();
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    try {
      await axios.post(`${API}/stock-issuances`, formData);
      toast.success('Stock issued successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
      fetchAlerts(); // Refresh alerts in case of low stock
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create issuance');
    }
  };

  const handleCancel = async () => {
    try {
      await axios.delete(`${API}/stock-issuances/${issuanceToCancel.id}`);
      toast.success('Issuance cancelled and inventory restored');
      setCancelDialogOpen(false);
      setIssuanceToCancel(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel issuance');
    }
  };

  const toggleRowExpand = (issuanceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [issuanceId]: !prev[issuanceId]
    }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const filteredIssuances = issuances.filter(issuance => 
    filterCustomer === 'all' || issuance.customer_id === filterCustomer
  );

  const totalCost = formData.items.reduce((sum, item) => sum + (item.quantity * item.buying_price), 0);
  const totalValue = formData.items.reduce((sum, item) => sum + (item.quantity * item.selling_price), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stock-issuance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Stock Issuance</h1>
          <p className="text-slate-500 mt-1">Issue parts to customers</p>
        </div>
        {canCreateIssuance() && (
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="new-issuance-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Issuance
          </Button>
        )}
      </div>

      {/* Filter */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger className="w-full md:w-64 rounded-sm" data-testid="customer-filter">
              <SelectValue placeholder="Filter by customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Issuances Table */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredIssuances.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Issuance #</TableHead>
                  <TableHead>Quotation #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Location</TableHead>
                  {canViewPrices() && <TableHead className="text-right">Cost</TableHead>}
                  {canViewPrices() && <TableHead className="text-right">Value</TableHead>}
                  {canViewPrices() && <TableHead className="text-right">Profit</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIssuances.map(issuance => (
                  <Collapsible key={issuance.id} open={expandedRows[issuance.id]} asChild>
                    <>
                      <TableRow 
                        className={issuance.status === 'cancelled' ? 'opacity-50' : ''}
                        data-testid={`issuance-row-${issuance.id}`}
                      >
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleRowExpand(issuance.id)}
                            >
                              {expandedRows[issuance.id] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {formatDate(issuance.issued_at)}
                        </TableCell>
                        <TableCell className="font-mono font-semibold text-slate-900">
                          {issuance.issuance_number}
                        </TableCell>
                        <TableCell className="font-mono text-blue-600">
                          {issuance.quotation_number}
                        </TableCell>
                        <TableCell>
                          <button
                            className="text-left hover:underline text-slate-900 font-medium"
                            onClick={() => navigate(`/customers/${issuance.customer_id}`)}
                          >
                            {issuance.customer_name}
                          </button>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="rounded-sm">
                            {issuance.location_name}
                          </Badge>
                        </TableCell>
                        {canViewPrices() && (
                          <TableCell className="text-right font-mono text-sm text-red-600">
                            {formatCurrency(issuance.total_cost)}
                          </TableCell>
                        )}
                        {canViewPrices() && (
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(issuance.total_value)}
                          </TableCell>
                        )}
                        {canViewPrices() && (
                          <TableCell className="text-right font-mono text-sm font-semibold text-emerald-600">
                            {formatCurrency(issuance.profit)}
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className={`rounded-sm ${
                            issuance.status === 'completed' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {issuance.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setViewingIssuance(issuance);
                                setViewDialogOpen(true);
                              }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {canDelete() && issuance.status === 'completed' && (
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => {
                                    setIssuanceToCancel(issuance);
                                    setCancelDialogOpen(true);
                                  }}
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel & Restore
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableCell colSpan={11} className="p-4">
                            <div className="border rounded-sm bg-white divide-y">
                              {issuance.items?.map((item, idx) => (
                                <div key={idx} className="p-3 flex justify-between items-center">
                                  <div className="flex items-center gap-3">
                                    <Package className="w-5 h-5 text-slate-400" />
                                    <div>
                                      <p className="font-mono font-semibold">{item.part_reference}</p>
                                      <p className="text-xs text-slate-500">{item.part_description}</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm">
                                      <span className="font-semibold">{item.quantity}</span> × {formatCurrency(item.selling_price)}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      Cost: {formatCurrency(item.buying_price)} each
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Package className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No stock issuances</p>
              <p className="text-sm">Issue parts to customers to see them here</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="view-issuance-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold font-mono">{viewingIssuance?.issuance_number}</DialogTitle>
            <DialogDescription>Stock issuance details</DialogDescription>
          </DialogHeader>
          {viewingIssuance && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="font-semibold">{viewingIssuance.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Quotation #</p>
                  <p className="font-mono font-semibold text-blue-600">{viewingIssuance.quotation_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-semibold">{viewingIssuance.location_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Issued By</p>
                  <p className="font-semibold">{viewingIssuance.issued_by}</p>
                </div>
              </div>
              
              <div className="border rounded-sm divide-y">
                {viewingIssuance.items?.map((item, idx) => (
                  <div key={idx} className="p-2 flex justify-between items-center">
                    <div>
                      <p className="font-mono text-sm font-semibold">{item.part_reference}</p>
                      <p className="text-xs text-slate-500">{item.part_description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{item.quantity} × {formatCurrency(item.selling_price)}</p>
                      <p className="text-xs text-slate-500">Cost: {formatCurrency(item.buying_price)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                <div className="text-center p-2 bg-red-50 rounded-sm">
                  <p className="text-xs text-slate-500">Cost</p>
                  <p className="font-semibold text-red-600">{formatCurrency(viewingIssuance.total_cost)}</p>
                </div>
                <div className="text-center p-2 bg-slate-50 rounded-sm">
                  <p className="text-xs text-slate-500">Value</p>
                  <p className="font-semibold">{formatCurrency(viewingIssuance.total_value)}</p>
                </div>
                <div className="text-center p-2 bg-emerald-50 rounded-sm">
                  <p className="text-xs text-slate-500">Profit</p>
                  <p className="font-semibold text-emerald-600">{formatCurrency(viewingIssuance.profit)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Issuance Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-issuance-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">New Stock Issuance</DialogTitle>
            <DialogDescription>Issue parts to a customer</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="issuance-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quotation Number *</Label>
                <Input
                  value={formData.quotation_number}
                  onChange={(e) => setFormData({ ...formData, quotation_number: e.target.value })}
                  placeholder="e.g., QT-2026-001"
                  required
                  className="rounded-sm"
                  data-testid="issuance-quotation-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Issue from Location *</Label>
              <Select
                value={formData.location_id}
                onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                required
              >
                <SelectTrigger className="rounded-sm" data-testid="issuance-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add Items */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-semibold">Parts to Issue</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Select
                    value={newItem.part_id}
                    onValueChange={(value) => {
                      const part = parts.find(p => p.id === value);
                      setNewItem({ ...newItem, part_id: value, selling_price: part?.selling_price || 0 });
                    }}
                  >
                    <SelectTrigger className="flex-1 rounded-sm">
                      <SelectValue placeholder="Select part" />
                    </SelectTrigger>
                    <SelectContent>
                      {parts.filter(p => p.quantity > 0).map(part => (
                        <SelectItem key={part.id} value={part.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{part.reference_number}</span>
                            <span className="text-slate-500">-</span>
                            <span className="truncate max-w-[150px]">{part.description}</span>
                            <Badge variant="secondary" className="ml-1">Qty: {part.quantity}</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    className="w-20 rounded-sm"
                    placeholder="Qty"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newItem.selling_price}
                    onChange={(e) => setNewItem({ ...newItem, selling_price: parseFloat(e.target.value) || 0 })}
                    className="w-28 rounded-sm"
                    placeholder={`Sell (${getCurrencySymbol()})`}
                  />
                  <Button type="button" onClick={handleAddItem} size="sm" className="rounded-sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.items.length > 0 && (
                  <div className="border rounded-sm divide-y">
                    {formData.items.map((item, idx) => (
                      <div key={idx} className="p-2 flex justify-between items-center">
                        <div>
                          <p className="font-mono text-sm font-semibold">{item.part_reference}</p>
                          <p className="text-xs text-slate-500">{item.part_description}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm">{item.quantity} × {formatCurrency(item.selling_price)}</p>
                            <p className="text-xs text-slate-500">Cost: {formatCurrency(item.buying_price)}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(idx)}
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="p-3 bg-slate-50 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-xs text-slate-500">Cost</p>
                        <p className="font-semibold text-red-600">{formatCurrency(totalCost)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Value</p>
                        <p className="font-semibold">{formatCurrency(totalValue)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Profit</p>
                        <p className="font-semibold text-emerald-600">{formatCurrency(totalValue - totalCost)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm"
                rows={2}
                placeholder="Optional notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="issuance-submit-btn">
                Issue Stock
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-issuance-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Cancel Stock Issuance</DialogTitle>
            <DialogDescription>
              This will cancel issuance "{issuanceToCancel?.issuance_number}" and restore all parts to inventory.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="rounded-sm">
              Keep
            </Button>
            <Button variant="destructive" onClick={handleCancel} className="rounded-sm" data-testid="confirm-cancel-btn">
              Cancel & Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockIssuance;
